import { cc } from "afms/theme/commandCenterTheme";

const item = {
  width: "100%",
  padding: 0,
  cursor: "pointer",
};

function itemContent(theme, ownerState) {
  const { palette, typography, transitions, functions } = theme;
  const { active, miniSidenav, name, nested, isAfms, extraIndentRem = 0 } = ownerState;

  const { dark, gradients } = palette;
  const { size, fontWeightMedium, fontWeightRegular } = typography;
  const { pxToRem, rgba } = functions;

  // Base left indent depending on whether the item is nested under a
  // parent collapse. `extraIndentRem` adds another step on top of that
  // for items flagged with `nested: true` in routes.js.
  const baseLeftRem = nested ? 44 / 16 : 10 / 16;
  const leftMarginRem = baseLeftRem + extraIndentRem;

  if (isAfms) {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "auto",
      padding: `${pxToRem(8)} ${pxToRem(12)}`,
      margin: `0 ${pxToRem(10)} ${pxToRem(4)} ${leftMarginRem}rem`,
      borderRadius: "8px",
      userSelect: "none",
      position: "relative",
      background: active ? cc.blue : "transparent",
      color: active ? "#fff" : "rgba(255,255,255,0.72)",
      boxShadow: active ? "0 10px 20px rgba(13,110,253,0.24)" : "none",
      transition: transitions.create(["background-color", "box-shadow", "color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
      "&:hover": {
        background: active ? cc.blue : "rgba(13,110,253,0.18)",
        color: "#fff",
      },
      "& span": {
        color: "inherit",
        fontWeight: active ? 800 : 600,
        fontSize: nested ? 12 : 13,
        lineHeight: 1.25,
        opacity: miniSidenav ? 0 : 1,
        transition: transitions.create(["opacity", "color"], {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
      },
      "&::before": {
        content: nested ? '""' : "none",
        width: pxToRem(5),
        height: pxToRem(5),
        backgroundColor: active ? "#fff" : "rgba(255,255,255,0.45)",
        display: miniSidenav ? "none" : "block",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        left: pxToRem(-12),
        borderRadius: "50%",
      },
    };
  }

  // Non-AFMS branch — 21.6px is the legacy default; add extraIndentRem
  // on top so the same `nested: true` route flag works in both contexts.
  const nonAfmsLeftRem = 21.6 / 16 + extraIndentRem;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: `${pxToRem(7.2)} ${pxToRem(16)}`,
    margin: `0 ${pxToRem(16)} 0 ${nonAfmsLeftRem}rem`,
    userSelect: "none",
    position: "relative",

    "& span": {
      color: active ? dark.main : rgba(gradients.dark.state, 0.7),
      fontWeight: active ? fontWeightMedium : fontWeightRegular,
      fontSize: size.sm,
      opacity: miniSidenav ? 0 : 1,
      transition: transitions.create(["opacity", "color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "&::before": {
      content: '""',
      width: () => {
        if (!miniSidenav) {
          return active ? pxToRem(8) : pxToRem(5);
        }

        return 0;
      },
      height: () => {
        if (!miniSidenav) {
          return active ? pxToRem(8) : pxToRem(5);
        }

        return 0;
      },
      backgroundColor: active ? dark.main : rgba(gradients.dark.state, 0.5),
      color: active ? dark.main : rgba(gradients.dark.state, 0.5),
      fontWeight: active ? fontWeightMedium : fontWeightRegular,
      display: "flex",
      alignItems: "center",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      left: pxToRem(-18),
      opacity: 1,
      borderRadius: "50%",
      fontSize: size.sm,
    },
  };
}

function itemArrow(theme, ownerState) {
  const { typography, transitions, functions, palette } = theme;
  const { open, miniSidenav, isAfms } = ownerState;

  const { dark, gradients } = palette;
  const { size } = typography;
  const { pxToRem, rgba } = functions;

  if (isAfms) {
    return {
      fontSize: `${size.md} !important`,
      fontWeight: 700,
      marginRight: pxToRem(-2.5),
      transform: open ? "rotate(0)" : "rotate(-180deg)",
      color: "inherit",
      opacity: miniSidenav ? 0 : 0.9,
      transition: transitions.create(["color", "transform", "opacity"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
    };
  }

  return {
    fontSize: `${size.md} !important`,
    fontWeight: 700,
    marginRight: pxToRem(-2.5),
    transform: () => {
      if (open) {
        return miniSidenav ? `translateX(${pxToRem(-24)}) rotate(0)` : "rotate(0)";
      }

      return miniSidenav ? `translateX(${pxToRem(-24)}) rotate(-180deg)` : "rotate(-180deg)";
    },
    color: open ? dark.main : rgba(gradients.dark.state, 0.4),
    transition: transitions.create(["color", "transform"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),
  };
}

export { item, itemContent, itemArrow };
